import { BreakPointCondition, useBreakpointIf, VStack } from '@genial/components';
import { BoxBorder } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { ScrollView } from 'native-base';
import React, { useCallback } from 'react';

import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import {
  CustomerDetailStackParamList,
  SignedInNavigationScreenNavigationProp,
} from '../../routes/types';
import { securitiesTransferDetailQuery } from '../../store/customerDetail/SecuritiesTransfer/selector';
import { TransferStatusHistoryItem } from '../../store/customerDetail/SecuritiesTransfer/types';
import { groupedCustomerDetailQuery } from '../../store/customerDetail/selector';

import { IOProductTables } from './components/IOProductTables';
import { IOProductTablesLoading } from './components/IOProductTablesLoading';
import { MainInfoContainer, MainInfoContainerLoading } from './components/MainInfoContainer';
import { MainStatusContainer, MainStatusContainerLoading } from './components/MainStatusContainer';
import { SecuritiesTransferDetailHeader } from './components/SecuritiesTransferDetailHeader';

export const SecuritiesTransferDetail: React.FC = () => {
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'SecuritiesTransferDetail'>>();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const {
    data: customerData,
    loading: customerLoading,
    error: customerError,
    refresher: customerRefresher,
  } = useRecoilQuery(groupedCustomerDetailQuery({ accountNumber: route.params.accountNumber }));

  const { data, loading } = useRecoilQuery(
    securitiesTransferDetailQuery({
      accountNumber: route.params.accountNumber,
      protocolNumber: route.params.protocolNumber,
    })
  );

  const handleOpenHistory = useCallback(
    (productName?: string, history?: TransferStatusHistoryItem[]) => {
      navigate('SecuritiesTransferHistoryModal', {
        protocolNumber: data?.data.protocolNormalized || '',
        customerName: data?.data.investorNameNormalized || '',
        customerSinacorAccount: data?.data.investorSinacorNormalized || '',
        customerCpfCnpj: data?.data.investorCpfCnpjNormalized || '',
        productName,
        history,
      });
    },
    [navigate, data]
  );

  const renderTables = useCallback(() => {
    if (loading) {
      return <IOProductTablesLoading />;
    }
    if (data) {
      return (
        <IOProductTables
          hasSelfOrigin={data.data.flowTypeNormalized === 'Outgoing'}
          fixedIncome={data.data.fixedIncome}
          funds={data.data.funds}
          equities={data.data.equities}
          dividends={data.data.dividends}
          treasury={data.data.treasury}
          onPressItem={(item) => handleOpenHistory(item.name, item.statusHistory)}
        />
      );
    }
    return null;
  }, [data, loading, handleOpenHistory]);

  const renderMainInfoContainer = useCallback(() => {
    if (loading) {
      return <MainInfoContainerLoading isMobile={isMobile} isTablet={isTablet} />;
    }
    if (data) {
      return (
        <MainInfoContainer
          hasSelfOrigin={data.data.flowTypeNormalized === 'Outgoing'}
          securitiesTransferItem={data.data}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      );
    }
    return null;
  }, [data, loading, isMobile, isTablet]);

  const renderMainStatusContainer = useCallback(() => {
    if (loading) {
      return <MainStatusContainerLoading isMobile={isMobile} />;
    }
    if (data) {
      return (
        <MainStatusContainer
          onDetailsPress={() => handleOpenHistory()}
          statusIcon={data.data.statusIcon}
          statusTitle={data.data.status}
          statusDescription={data.data.statusDescriptionNormalized}
          isMobile={isMobile}
        />
      );
    }
    return null;
  }, [data, loading, isMobile, handleOpenHistory]);

  const handleClose = useCallback(() => {
    navigate('CustomerDetailGroup', {
      ...route.params,
      selectedTab: 'transferencia-de-valores',
    });
  }, [navigate, route.params]);

  return (
    <ScrollView
      w="full"
      flex={1}
      scrollEventThrottle={200}
      px={Utils.Responsive.All({
        desktop: 'inline.xxxs',
        tablet: 'inline.xxs',
        phone: 'inline.xxxs',
      })}
      py={Utils.Responsive.PhoneTablet({
        tablet: 0,
        phone: 'inline.xxxs',
      })}
      pb={Utils.Responsive.PhoneTablet({
        tablet: 0,
        phone: 'stack.xxxl',
      })}>
      <VStack
        width="100%"
        maxWidth="1280px"
        minWidth="0"
        alignSelf="center"
        space={Utils.Responsive.PhoneTablet({
          phone: 'stack.xxxs',
          tablet: 'stack.xxs',
        })}
        {...(isMobile && { space: 'stack.nano' })}>
        <SecuritiesTransferDetailHeader
          isMobile={isMobile}
          onReturn={handleClose}
          customerLoading={customerLoading}
          customerError={!!customerError}
          customerRefresher={customerRefresher}
          customerData={customerData}
        />
        <VStack
          p={isMobile ? '0' : 'inset.xs'}
          background={isMobile ? 'transparent' : 'neutral.white'}
          borderRadius={BoxBorder.heavy}>
          {renderMainStatusContainer()}
          {renderMainInfoContainer()}
          {renderTables()}
        </VStack>
      </VStack>
    </ScrollView>
  );
};
